import React from 'react';

import { NvButton, NvTooltip } from '@simwood/nv-react-components';

import * as styles from './styles.module.scss';

import { BsMicrosoftTeams } from 'react-icons/bs';
import {
  FaExclamationCircle,
  FaEyeSlash,
  FaGlobe,
  FaRegCheckCircle,
  FaRegTimesCircle,
  FaRegTrashAlt,
  FaTag,
} from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { ApiForwardingRule } from '../../../../shared/types/ForwardingRule';
import { StoreState } from '../../../store';
import GroupIcon from '../../icons/GroupIcon';

const TimeIntervalRemovedNode = () => (
  <NvTooltip
    content={
      <span>
        This time interval has likely been removed, consider selecting another
        time interval
      </span>
    }
  >
    <>
      <FaExclamationCircle style={{ color: 'var(--danger)' }} />
      <span style={{ marginLeft: '5px' }}>Unknown</span>
    </>
  </NvTooltip>
);

const formattedStatus = {
  BUSY: 'Busy',
  FAILURE: 'Failure',
  NOANSWER: 'Unanswered',
};

const formatForwardingRule = (rule: ApiForwardingRule) => {
  const { action, destination } = rule;
  // if (!callOutcome) {
  // If call status is 'any'
  if (action === 'FORWARD') {
    return `Forward to ${destination}`;
  } else if (action === 'FOLLOWME') {
    return (
      <>
        <span>Also ring</span>
        <span className={styles.destination}>
          {rule.platform === 'teams' && <BsMicrosoftTeams />}
          <span>{destination}</span>
        </span>
      </>
    );
  } else if (action === 'VOICEMAIL') {
    return `Send all calls straight to voicemail`;
  }
  return '';
};

interface Props {
  forwardingRule: ApiForwardingRule;
  onClick: (fr: ApiForwardingRule) => void;
  toggleEnabled: (fr: ApiForwardingRule) => void;
  remove: (fr: ApiForwardingRule) => void;
}

const ForwardingRuleCard: React.FC<Props> = ({
  toggleEnabled,
  remove,
  forwardingRule,
  onClick,
}): JSX.Element => {
  const timeIntervalsById = useSelector(
    (state: StoreState) => state.timeIntervals.timeIntervalsById,
  );
  const timeInterval = timeIntervalsById[forwardingRule.timeInterval];
  const EnabledIcon = forwardingRule.enabled
    ? FaRegCheckCircle
    : FaRegTimesCircle;
  return (
    <tr
      key={forwardingRule.id}
      className={styles.row}
      onClick={() => onClick(forwardingRule)}
    >
      <td>
        <EnabledIcon
          onClick={(e) => {
            e.stopPropagation();
            toggleEnabled(forwardingRule);
          }}
          className={
            styles[forwardingRule.enabled ? 'enabled_icon' : 'disabled_icon']
          }
        />
      </td>
      <td>{timeInterval ? timeInterval.name : <TimeIntervalRemovedNode />}</td>
      <td>
        {forwardingRule.callOutcome
          ? formattedStatus[forwardingRule.callOutcome]
          : 'Any'}
      </td>
      <td>{formatForwardingRule(forwardingRule)}</td>
      <td>
        <span
          className={
            styles[
              forwardingRule.applyGroup ? 'status_enabled' : 'status_disabled'
            ]
          }
        >
          <GroupIcon />
        </span>
        <span
          className={
            styles[
              forwardingRule.preserveId ? 'status_enabled' : 'status_disabled'
            ]
          }
        >
          <FaTag />
        </span>
        <span
          className={
            styles[
              forwardingRule.screeningEnabled
                ? 'status_enabled'
                : 'status_disabled'
            ]
          }
        >
          <FaEyeSlash />
        </span>
        <span
          className={
            styles[
              forwardingRule.externalOnly ? 'status_enabled' : 'status_disabled'
            ]
          }
        >
          <FaGlobe />
        </span>
      </td>
      <td>
        {forwardingRule.key && (
          <>
            <span className={styles.muted}>*74</span>
            <span>{forwardingRule.key}</span>
          </>
        )}
      </td>
      <td>
        <NvButton
          mini
          inverted
          borderless
          leftIcon={<FaRegTrashAlt />}
          disabled={forwardingRule.platform === 'teams'}
          onClick={(e) => {
            e.stopPropagation();
            remove(forwardingRule);
          }}
        />
      </td>
    </tr>
  );
};

export default ForwardingRuleCard;
